import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Percentage from "../../assets/images/sidebar/Percentage";
import Location from "../../assets/images/table/Location";
import Number from "../../assets/images/table/NumberKey";
import Store from "../../assets/images/table/Store";
import DataTable, {
  Column,
} from "../../components/common/data-table/DataTable";
import TitleUpdater from "../../components/common/title/TitleUpdater";
import styles from "./StoresPercentages.module.scss";
import { useAuth } from "../../context/AuthContext";
import StoresService from "../../api/services/stores.service";
import Modal from "../../components/common/modal/Modal";
import Input from "../../components/common/input/Input";

const StoresPercentages = () => {
  const { t } = useTranslation("translation");
  const { setButtons } = useAuth();

  const columns: Column[] = [
    {
      key: "name",
      header: <Store />,
      render: (_, row) => (
        <div className={styles.user_cell}>
          {row?.logo_url ? (
            <img
              src={row?.logo_url}
              alt={row?.name}
              className={styles.user_avatar}
            />
          ) : (
            <div className={styles.no_image}>
              {row?.name[0]}
              {row?.name[1]}
            </div>
          )}
          <div className={styles.user_info}>
            <p className={styles.user_name}>{row.name}</p>
            <a href={`tel:${row?.contact_phone}`} className={styles.user_phone}>
              {row?.contact_phone || ""}
            </a>
            <a href={`mailto:${row?.email}`} className={styles.user_email}>
              {row?.email}
            </a>
          </div>
        </div>
      ),
      filter: true,
    },
    { key: "contact_phone", header: <Number /> },
    { key: "business_address", header: <Location /> },
    {
      key: "percent",
      header: <Percentage />,
      filter: true,
      render: (v) => `${v} %`,
    },
  ];

  interface StoreType {
    id: number;
    name: string;
    email: string;
    logo_url: string;
    contact_phone: string;
    business_address: string;
    percent: number;
  }

  const [stores, setStores] = useState<StoreType[]>([]);
  const [loading, setLoading] = useState(false);
  const { searchValue } = useAuth();
  const [count, setCount] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState<StoreType | null>(null);
  const [percentValue, setPercentValue] = useState("");

  const getStores = useCallback(async () => {
    if (!searchValue) setLoading(true);
    try {
      const res = await StoresService.getALLStores(page, limit, searchValue);
      setStores(res.data.data);
      setCount(res.data.count);
    } catch (error) {
      console.error("Error fetching stores:", error);
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchValue]);

  useEffect(() => {
    getStores();
  }, [getStores]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1) return;
    if (count && newPage > Math.ceil(count / limit)) return;
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleEdit = (id: number) => {
    const store = stores.find((store) => store.id === id);
    if (store) {
      setSelectedStore(store);
      setPercentValue(store.percent.toString());
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedStore(null);
    setPercentValue("");
  };

  const updatePercentByStore = async () => {
    if (!selectedStore || !percentValue) return;
    try {
      const formData = new FormData();
      formData.append("percent", percentValue);
      await StoresService.updatePercentage(selectedStore.id, formData);
      await getStores();
      handleCloseModal();
    } catch (error) {
      console.error("Error updating percentage:", error);
    }
  };

  useEffect(() => {
    setButtons([]);
    return () => setButtons([]);
  }, [setButtons]);

  return (
    <div className={styles.stores_percentages}>
      <TitleUpdater title={t("stores_percentages.title") as string} />
      <div>
        <DataTable
          table_title={t("stores_percentages.title") as string}
          data={stores}
          columns={columns}
          onEdit={handleEdit}
          actions
          other
          loading={loading}
          paginationData={{
            total: count,
            page,
            limit,
            onPageChange: handlePageChange,
            onLimitChange: handleLimitChange,
          }}
        />
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={updatePercentByStore}
        title={`${t("stores_percentages.edit_title") as string} ${selectedStore?.name || ""}`}
      >
        <div className={styles.modal_content}>
          <Input
            label={t("stores_percentages.form.percentage") as string}
            type="number"
            value={percentValue}
            onChange={(e) => setPercentValue(e.target.value)}
            placeholder={t("stores_percentages.form.placeholder") as string}
            min="0"
            max="100"
          />
        </div>
      </Modal>
    </div>
  );
};

export default StoresPercentages;
